import { DateTime } from "luxon";
import type {
  AdminGetRoutesLocationsResDto,
  AdminGetRoutesResDto,
} from "service/api/estabrookSchemas";
import { getRouteStart, getTimelineDate } from "./route-start";
import { getRouteStopTimings } from "./route-estimate";
import { isSkippedStop, stopServiceMinutes } from "./stop-service-minutes";

const isDone = (location: AdminGetRoutesLocationsResDto) =>
  location.driverStatus === "completed" ||
  location.driverStatus === "partiallyCompleted" ||
  location.driverStatus === "cantPlow";

/** Display-only timings: recorded completions must not wait for an ETA refresh. */
export const getLiveTimelineLocations = (
  route: Pick<
    AdminGetRoutesResDto,
    "state" | "date" | "startTime" | "locations"
  >,
  timelineDate = getTimelineDate([route]),
): AdminGetRoutesLocationsResDto[] => {
  const locations = route.locations ?? [];
  if (route.state !== "live") return locations;

  const routeStart = getRouteStart(route.startTime, timelineDate);
  if (!routeStart.isValid) return locations;
  const recorded = new Map<string, { arrival: number; completion: number }>();

  locations.forEach((location) => {
    if (!isDone(location) || isSkippedStop(location)) return;
    const end = DateTime.fromISO(
      location.driverServiceEndTime || location.driverStatusLastUpdatedAt || "",
    );
    if (!end.isValid || end < routeStart) return;
    const start = DateTime.fromISO(location.driverServiceStartTime || "");
    const arrival =
      start.isValid && start >= routeStart && start <= end ? start : end;
    recorded.set(location.locationId, {
      arrival: arrival.diff(routeStart, "minutes").minutes,
      completion: end.diff(routeStart, "minutes").minutes,
    });
  });
  const inProgress = locations.filter(
    (location) =>
      location.driverStatus === "inProgress" && !isSkippedStop(location),
  );
  if (recorded.size === 0 && inProgress.length === 0) return locations;

  // Read cumulative values before reordering; they belong to the server's order.
  const serviceMinutes = stopServiceMinutes(locations);
  const originalTimings = getRouteStopTimings(locations);
  const finished = locations
    .filter((location) => isDone(location) && !isSkippedStop(location))
    .sort((a, b) => {
      const completion = (location: AdminGetRoutesLocationsResDto) =>
        recorded.get(location.locationId)?.completion ??
        originalTimings[location.locationId].completionMinutes;
      return completion(a) - completion(b);
    });
  const pending = locations.filter(
    (location) =>
      !isDone(location) &&
      location.driverStatus !== "inProgress" &&
      !isSkippedStop(location),
  );
  let elapsed = 0;
  let cumulativeService = 0;
  const timeline = [...finished, ...inProgress, ...pending].map((location) => {
    const actual = recorded.get(location.locationId);
    const activeStart = DateTime.fromISO(location.driverServiceStartTime || "");
    const activeArrival =
      location.driverStatus === "inProgress" &&
      activeStart.isValid &&
      activeStart >= routeStart
        ? activeStart.diff(routeStart, "minutes").minutes
        : undefined;
    // Bulk admin updates can overlap recorded service intervals. Keep their
    // completion instant while preventing negative travel/service widths.
    const arrival = actual
      ? Math.max(elapsed, actual.arrival)
      : activeArrival !== undefined
        ? Math.max(elapsed, activeArrival)
        : elapsed + (location.travelDetails?.travelTimeBetweenLocations ?? 0);
    const duration = actual
      ? Math.max(0, actual.completion - arrival)
      : serviceMinutes[location.locationId];
    const travel = arrival - elapsed;
    elapsed = arrival + duration;
    cumulativeService += duration;
    return {
      ...location,
      travelDetails: {
        ...location.travelDetails,
        estimatedTimeMinutes: arrival,
        serviceTimeMin: cumulativeService,
        travelTimeBetweenLocations: travel,
      },
    };
  });
  return [...timeline, ...locations.filter(isSkippedStop)];
};
